'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import Hls from 'hls.js';
import { X, Radio, Eye, Wifi, WifiOff, AlertTriangle, RefreshCw, WifiOff as NetworkOff, Film, Clock, ShieldAlert, MessageCircle, Send, LogIn } from 'lucide-react';
import Link from 'next/link';
import { Stream } from '@/lib/services/streamService';
import { useAuth } from '@/contexts/AuthContext';

interface StreamPlayerModalProps {
  stream: Stream | null;
  onClose: () => void;
}

// ─── Error Types ────────────────────────────────────────────────────────────

type StreamErrorCode =
  | 'NETWORK_ERROR' |'MANIFEST_ERROR' |'DECODE_ERROR' |'TIMEOUT_ERROR' |'UNSUPPORTED_ERROR' |'MEDIA_ERROR' |'UNKNOWN_ERROR';

interface StreamError {
  code: StreamErrorCode;
  title: string;
  message: string;
  retryable: boolean;
}

const ERROR_DEFINITIONS: Record<StreamErrorCode, Omit<StreamError, 'code'>> = {
  NETWORK_ERROR: {
    title: 'Network Error',
    message: 'Could not reach the stream server. Check your internet connection or the stream may be temporarily unavailable.',
    retryable: true,
  },
  MANIFEST_ERROR: {
    title: 'Stream Not Found',
    message: 'The stream playlist could not be loaded. The channel may be offline or the stream URL is invalid.',
    retryable: true,
  },
  DECODE_ERROR: {
    title: 'Playback Error',
    message: 'Your browser could not decode the stream. The stream format may be unsupported or the data is corrupted.',
    retryable: false,
  },
  TIMEOUT_ERROR: {
    title: 'Stream Timed Out',
    message: 'The stream took too long to respond. The server may be overloaded or your connection is too slow.',
    retryable: true,
  },
  UNSUPPORTED_ERROR: {
    title: 'Unsupported Format',
    message: 'HLS playback is not supported in this browser. Try using Chrome, Firefox, or Safari.',
    retryable: false,
  },
  MEDIA_ERROR: {
    title: 'Media Error',
    message: 'A media error occurred during playback. The stream data may be corrupted or the format is incompatible.',
    retryable: true,
  },
  UNKNOWN_ERROR: {
    title: 'Playback Failed',
    message: 'An unexpected error occurred while loading the stream. Please try again.',
    retryable: true,
  },
};

function buildStreamError(code: StreamErrorCode): StreamError {
  return { code, ...ERROR_DEFINITIONS[code] };
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatViewers(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

function isHlsUrl(url: string): boolean {
  return url.includes('.m3u8');
}

function isDirectVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg)(\?|$)/i.test(url);
}

function toProxiedUrl(url: string): string {
  if (url.startsWith('http://')) {
    const withoutScheme = url.replace(/^http:\/\//, '');
    return `/api/stream-proxy/${withoutScheme}`;
  }
  return url;
}

// ─── Error Icon ─────────────────────────────────────────────────────────────

function ErrorIcon({ code }: { code: StreamErrorCode }) {
  const cls = 'w-6 h-6';
  switch (code) {
    case 'NETWORK_ERROR':
      return <NetworkOff className={`${cls} text-amber-400`} />;
    case 'MANIFEST_ERROR':
      return <WifiOff className={`${cls} text-red-400`} />;
    case 'TIMEOUT_ERROR':
      return <Clock className={`${cls} text-amber-400`} />;
    case 'DECODE_ERROR': case'MEDIA_ERROR':
      return <Film className={`${cls} text-red-400`} />;
    case 'UNSUPPORTED_ERROR':
      return <ShieldAlert className={`${cls} text-[rgb(113,113,133)]`} />;
    default:
      return <AlertTriangle className={`${cls} text-amber-400`} />;
  }
}

// ─── Error Display ───────────────────────────────────────────────────────────

interface ErrorDisplayProps {
  error: StreamError;
  retryCount: number;
  maxRetries: number;
  onRetry: () => void;
}

function ErrorDisplay({ error, retryCount, maxRetries, onRetry }: ErrorDisplayProps) {
  const canRetry = error.retryable && retryCount < maxRetries;
  const exhausted = error.retryable && retryCount >= maxRetries;

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-center px-6 bg-[rgb(9,9,11)]">
      {/* Icon */}
      <div className="w-16 h-16 rounded-full bg-[rgb(26,26,32)] border border-[rgb(39,39,48)] flex items-center justify-center">
        <ErrorIcon code={error.code} />
      </div>

      {/* Title & message */}
      <div className="space-y-1.5 max-w-xs">
        <p className="text-white font-semibold text-base">{error.title}</p>
        <p className="text-sm text-[rgb(113,113,133)] leading-relaxed">{error.message}</p>
      </div>

      {/* Retry badge */}
      {retryCount > 0 && (
        <span className="text-[10px] text-[rgb(113,113,133)] bg-[rgb(26,26,32)] border border-[rgb(39,39,48)] px-2.5 py-1 rounded-full">
          Attempt {retryCount} of {maxRetries}
        </span>
      )}

      {/* Exhausted notice */}
      {exhausted && (
        <p className="text-xs text-red-400 max-w-xs">
          Maximum retry attempts reached. The stream appears to be unavailable.
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-3 mt-1">
        {canRetry && (
          <button
            onClick={onRetry}
            className="flex items-center gap-2 text-xs font-medium text-white bg-[rgb(232,82,26)] hover:bg-[rgb(200,65,15)] px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw size={13} />
            Retry
          </button>
        )}
        {exhausted && (
          <button
            onClick={onRetry}
            className="flex items-center gap-2 text-xs font-medium text-[rgb(113,113,133)] hover:text-white border border-[rgb(39,39,48)] hover:border-[rgb(63,63,78)] px-4 py-2 rounded-lg transition-colors"
          >
            <RefreshCw size={13} />
            Try Again
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Loading Spinner ─────────────────────────────────────────────────────────

function LoadingOverlay() {
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-[rgb(9,9,11)]">
      <div className="w-10 h-10 rounded-full border-2 border-[rgb(39,39,48)] border-t-[rgb(232,82,26)] animate-spin" />
      <p className="text-xs text-[rgb(113,113,133)]">Connecting to stream…</p>
    </div>
  );
}

// ─── HLS Video Player ────────────────────────────────────────────────────────

const MAX_RETRIES = 3;
const LOAD_TIMEOUT_MS = 15000; // 15 s

function HlsVideoPlayer({ src, streamName }: { src: string; streamName: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [streamError, setStreamError] = useState<StreamError | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const retryCountRef = useRef(0);

  const clearLoadTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const destroyHls = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }
  }, []);

  const captureError = useCallback((code: StreamErrorCode) => {
    clearLoadTimeout();
    setLoading(false);
    setStreamError(buildStreamError(code));
  }, [clearLoadTimeout]);

  const initPlayer = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;

    destroyHls();
    setStreamError(null);
    setLoading(true);

    // Always proxy http:// URLs to avoid mixed-content blocks
    const proxiedSrc = toProxiedUrl(src);

    // ── Timeout watchdog ──────────────────────────────────────────────────
    timeoutRef.current = setTimeout(() => {
      captureError('TIMEOUT_ERROR');
      destroyHls();
    }, LOAD_TIMEOUT_MS);

    if (isHlsUrl(src)) {
      if (Hls.isSupported()) {
        const hls = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          maxBufferLength: 30,
          // Allow cross-origin requests without credentials
          xhrSetup: (xhr) => { xhr.withCredentials = false; },
        });
        hlsRef.current = hls;

        hls.loadSource(proxiedSrc);
        hls.attachMedia(video);

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          clearLoadTimeout();
          setLoading(false);
          video.play().catch(() => {/* autoplay blocked */});
        });

        hls.on(Hls.Events.ERROR, (_event, data) => {
          if (!data.fatal) return;

          clearLoadTimeout();

          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              if (
                data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR ||
                data.details === Hls.ErrorDetails.MANIFEST_LOAD_TIMEOUT
              ) {
                captureError('MANIFEST_ERROR');
              } else {
                captureError('NETWORK_ERROR');
              }
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              captureError('MEDIA_ERROR');
              break;
            default:
              captureError('UNKNOWN_ERROR');
          }
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Safari native HLS — use proxied URL for http:// streams
        video.src = proxiedSrc;
        video.addEventListener('loadedmetadata', () => {
          clearLoadTimeout();
          setLoading(false);
        }, { once: true });
        video.play().catch(() => {});
      } else {
        clearLoadTimeout();
        captureError('UNSUPPORTED_ERROR');
      }
    } else if (isDirectVideoUrl(src)) {
      // Direct video file
      video.src = proxiedSrc;
      video.play().catch(() => {});
    } else {
      // Unknown format — try loading directly
      video.src = proxiedSrc;
      video.play().catch(() => {});
    }

    // ── Native video element error handler ────────────────────────────────
    const handleVideoError = () => {
      clearLoadTimeout();
      const mediaErr = video.error;
      if (!mediaErr) return;
      switch (mediaErr.code) {
        case MediaError.MEDIA_ERR_NETWORK:
          captureError('NETWORK_ERROR');
          break;
        case MediaError.MEDIA_ERR_DECODE:
          captureError('DECODE_ERROR');
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          captureError('UNSUPPORTED_ERROR');
          break;
        default:
          captureError('UNKNOWN_ERROR');
      }
    };

    const handleCanPlay = () => {
      clearLoadTimeout();
      setLoading(false);
    };

    video.addEventListener('error', handleVideoError);
    video.addEventListener('canplay', handleCanPlay);

    return () => {
      video.removeEventListener('error', handleVideoError);
      video.removeEventListener('canplay', handleCanPlay);
    };
  }, [src, captureError, clearLoadTimeout, destroyHls]);

  // Init on mount / src change
  useEffect(() => {
    retryCountRef.current = 0;
    setRetryCount(0);
    const cleanup = initPlayer();
    return () => {
      clearLoadTimeout();
      destroyHls();
      cleanup?.();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);

  const handleRetry = useCallback(() => {
    const next = retryCountRef.current + 1;
    retryCountRef.current = next;
    setRetryCount(next);
    initPlayer();
  }, [initPlayer]);

  return (
    <div className="relative w-full h-full">
      <video
        ref={videoRef}
        controls
        autoPlay
        playsInline
        crossOrigin="anonymous"
        className={`w-full h-full object-contain bg-black transition-opacity duration-300 ${
          streamError || loading ? 'opacity-0 pointer-events-none' : 'opacity-100'
        }`}
        title={streamName}
      />
      {loading && !streamError && <LoadingOverlay />}
      {streamError && (
        <ErrorDisplay
          error={streamError}
          retryCount={retryCount}
          maxRetries={MAX_RETRIES}
          onRetry={handleRetry}
        />
      )}
    </div>
  );
}

// ─── Comments Section ────────────────────────────────────────────────────────

interface Comment {
  id: string;
  author: string;
  text: string;
  timestamp: Date;
}

function CommentsSection({ streamId }: { streamId: string }) {
  const { user } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = commentText.trim();
    if (!trimmed || !user) return;
    const newComment: Comment = {
      id: `${Date.now()}`,
      author: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Viewer',
      text: trimmed,
      timestamp: new Date(),
    };
    setComments((prev) => [newComment, ...prev]);
    setCommentText('');
  };

  return (
    <div className="border-t border-[rgb(39,39,48)] px-4 py-3">
      <div className="flex items-center gap-2 mb-3">
        <MessageCircle size={14} className="text-[rgb(113,113,133)]" />
        <span className="text-xs font-semibold text-[rgb(161,161,178)] uppercase tracking-wide">Live Chat</span>
      </div>

      {/* Comment list */}
      {comments.length > 0 && (
        <div className="mb-3 space-y-2 max-h-32 overflow-y-auto">
          {comments.map((c) => (
            <div key={c.id} className="flex gap-2">
              <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[rgb(232,82,26)] flex items-center justify-center text-[10px] font-bold text-white">
                {c.author[0]?.toUpperCase()}
              </div>
              <div>
                <span className="text-xs font-semibold text-white">{c.author} </span>
                <span className="text-xs text-[rgb(161,161,178)]">{c.text}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Input or sign-in prompt */}
      {user ? (
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <input
            type="text"
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Say something..."
            maxLength={200}
            className="flex-1 bg-[rgb(26,26,32)] border border-[rgb(39,39,48)] text-sm text-white placeholder-[rgb(113,113,133)] rounded-lg px-3 py-1.5 focus:outline-none focus:border-[rgb(232,82,26)] transition-colors"
          />
          <button
            type="submit"
            disabled={!commentText.trim()}
            className="p-1.5 rounded-lg bg-[rgb(232,82,26)] hover:bg-[rgb(210,70,20)] disabled:opacity-40 disabled:cursor-not-allowed text-white transition-colors flex-shrink-0"
          >
            <Send size={14} />
          </button>
        </form>
      ) : (
        <div className="flex items-center justify-between bg-[rgb(26,26,32)] border border-[rgb(39,39,48)] rounded-lg px-3 py-2.5">
          <div className="flex items-center gap-2">
            <MessageCircle size={14} className="text-[rgb(113,113,133)]" />
            <span className="text-xs text-[rgb(113,113,133)]">Sign in to join the conversation</span>
          </div>
          <Link
            href="/sign-up-login-screen"
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-[rgb(232,82,26)] hover:bg-[rgb(210,70,20)] px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
          >
            <LogIn size={12} />
            Sign In
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── Main Modal ──────────────────────────────────────────────────────────────

export default function StreamPlayerModal({ stream, onClose }: StreamPlayerModalProps) {
  const backdropRef = useRef<HTMLDivElement>(null);
  const [isLandscapeMobile, setIsLandscapeMobile] = useState(false);

  useEffect(() => {
    if (!stream) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [stream, onClose]);

  // Detect mobile landscape orientation
  useEffect(() => {
    const checkOrientation = () => {
      const isLandscape = window.matchMedia('(orientation: landscape) and (max-height: 500px)').matches;
      setIsLandscapeMobile(isLandscape);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);
    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, []);

  if (!stream) return null;

  const isLive = stream.streamStatus === 'live';
  const isDegraded = stream.streamStatus === 'degraded';
  const isOffline = stream.streamStatus === 'offline';

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  const renderPlayer = () => {
    if (isOffline) {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-center px-6">
          <div className="w-14 h-14 rounded-full bg-[rgb(26,26,32)] flex items-center justify-center">
            <WifiOff size={24} className="text-[rgb(63,63,78)]" />
          </div>
          <p className="text-white font-semibold">Stream Offline</p>
          <p className="text-sm text-[rgb(113,113,133)] max-w-xs">
            This stream is currently offline. Check back later or try another channel.
          </p>
        </div>
      );
    }

    if (stream.streamUrl) {
      const url = stream.streamUrl;

      if (isHlsUrl(url) || isDirectVideoUrl(url)) {
        return <HlsVideoPlayer src={url} streamName={stream.name} />;
      }

      return (
        <iframe
          src={url}
          className="w-full h-full"
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          title={`${stream.name} live stream`}
        />
      );
    }

    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
        {stream.thumbnail && (
          <img
            src={stream.thumbnail}
            alt={stream.thumbnailAlt ?? stream.name}
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
        )}
        <div className="relative z-10 flex flex-col items-center gap-3 text-center px-6">
          <div className="w-16 h-16 rounded-full bg-[rgba(232,82,26,0.15)] border border-[rgba(232,82,26,0.3)] flex items-center justify-center">
            <Radio size={28} className="text-[rgb(232,82,26)]" />
          </div>
          <p className="text-white font-semibold text-lg">{stream.name}</p>
          <p className="text-sm text-[rgb(113,113,133)] max-w-sm">
            No stream URL configured. Add an HLS (.m3u8) or embed URL in Content Management to enable playback.
          </p>
          {isDegraded && (
            <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mt-1">
              <Wifi size={14} className="text-amber-400 flex-shrink-0" />
              <p className="text-xs text-amber-300">Stream quality is degraded — playback may be unstable.</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div
      ref={backdropRef}
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      style={isLandscapeMobile ? { padding: 0 } : undefined}
    >
      <div
        className="relative w-full max-w-3xl bg-[rgb(18,18,22)] border border-[rgb(39,39,48)] rounded-2xl overflow-hidden shadow-2xl"
        style={isLandscapeMobile ? { maxWidth: '100%', height: '100%', borderRadius: 0, border: 'none', display: 'flex', flexDirection: 'column' } : undefined}
      >
        {/* Header — hidden in mobile landscape */}
        {!isLandscapeMobile && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-[rgb(39,39,48)]">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[rgb(26,26,32)] flex items-center justify-center text-base">
                {stream.flag ?? '📺'}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-white truncate">{stream.name}</p>
                <p className="text-xs text-[rgb(113,113,133)]">
                  {stream.country} · {stream.category}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {isLive && (
                <span className="flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  <span className="w-1.5 h-1.5 rounded-full bg-white live-pulse" />
                  LIVE
                </span>
              )}
              {isDegraded && (
                <span className="flex items-center gap-1 bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                  ⚠ DEGRADED
                </span>
              )}
              {isOffline && (
                <span className="flex items-center gap-1 bg-[rgb(63,63,78)] text-[rgb(161,161,178)] text-[10px] font-bold px-2 py-0.5 rounded-full">
                  OFFLINE
                </span>
              )}
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-[rgb(113,113,133)] hover:text-white hover:bg-[rgb(39,39,48)] transition-colors"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Player area */}
        <div
          className="relative bg-[rgb(9,9,11)]"
          style={isLandscapeMobile ? { flex: 1, width: '100%' } : { aspectRatio: '16/9' }}
        >
          {/* Close button overlay in landscape mobile */}
          {isLandscapeMobile && (
            <button
              onClick={onClose}
              className="absolute top-2 right-2 z-10 p-1.5 rounded-lg bg-black/60 text-white hover:bg-black/80 transition-colors"
            >
              <X size={18} />
            </button>
          )}
          {renderPlayer()}
        </div>

        {/* Footer stats — hidden in mobile landscape */}
        {!isLandscapeMobile && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[rgb(39,39,48)]">
            <div className="flex items-center gap-4">
              {stream.streamStatus !== 'offline' && (
                <div className="flex items-center gap-1.5 text-xs text-[rgb(113,113,133)]">
                  <Eye size={12} />
                  <span className="tabular-nums">{formatViewers(stream.viewers)} viewers</span>
                </div>
              )}
              {stream.language && (
                <span className="text-xs text-[rgb(113,113,133)]">{stream.language}</span>
              )}
              {stream.streamUrl && (isHlsUrl(stream.streamUrl) || isDirectVideoUrl(stream.streamUrl)) && (
                <span className="text-[10px] font-medium text-emerald-400 bg-emerald-400/10 px-1.5 py-0.5 rounded">
                  {isHlsUrl(stream.streamUrl) ? 'HLS' : 'VIDEO'}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="text-xs text-[rgb(113,113,133)] hover:text-white transition-colors px-3 py-1.5 rounded-lg hover:bg-[rgb(39,39,48)]"
            >
              Close
            </button>
          </div>
        )}

        {/* Comments section — hidden in mobile landscape */}
        {!isLandscapeMobile && <CommentsSection streamId={stream.id} />}
      </div>
    </div>
  );
}
